// LIVE DATA — Performance › Sales: the whole Stripe account, read on every
// visit through the same path as the lead page's Payments panel (payments.js):
// GET /api/payments?view=sales → the sales stack's customer-payments lambda,
// which checks the caller's sign-in and workspace membership → production's
// read-only customer-payments-lookup function ({"action": "sales"}), which
// holds the Stripe key and only ever sends GET.
//
// What comes back is every subscription, invoice, charge, refund and dispute
// the account has, in Stripe's units (smallest currency unit, unix seconds),
// with each subscription's MRR already worked out the way Stripe Billing
// does. salesMetrics.js turns it into the page's numbers.
import { getIdToken } from './cognito.js'

// Same escape hatch as payments.js: `npm run start` without API_PROXY answers
// /api/* with index.html, so a non-JSON response means "no API on this
// origin" — retry the deployed stage directly.
const FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'
const QUERY = 'payments?view=sales'

// Switching the range re-reads nothing: the whole account comes back in one
// answer, so a read is reused for a minute. Production keeps its own for two.
const REUSE_MS = 60 * 1000
let last = null // { at, promise }

/**
 * The account's Stripe records. Throws an Error carrying `status` (0 when the
 * API could not be reached) and the server's own words, so the page can tell
 * an expired session (401) from a non-member (403) from Stripe being
 * unreadable (502).
 */
export function fetchSalesAccount({ fresh = false } = {}) {
  if (!fresh && last && Date.now() - last.at < REUSE_MS) return last.promise
  const promise = read()
  last = { at: Date.now(), promise }
  // A failed read is not reused: the next visit asks again.
  promise.catch(() => {
    if (last && last.promise === promise) last = null
  })
  return promise
}

async function read() {
  const token = await getIdToken()
  const init = { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  const get = async (url) => {
    try {
      return await fetch(url, init)
    } catch {
      return null
    }
  }
  const isJson = (r) => (r ? (r.headers.get('content-type') || '').includes('application/json') : false)
  let res = await get(`/api/${QUERY}`)
  if (!isJson(res)) res = await get(`${FALLBACK_BASE}/${QUERY}`)
  if (!res) {
    const err = new Error('Stripe could not be reached from this browser.')
    err.status = 0
    throw err
  }
  if (!isJson(res)) {
    const err = new Error(`Stripe is unavailable (${res.status}).`)
    err.status = res.status
    throw err
  }
  const data = await res.json()
  if (!res.ok) {
    const err = new Error(data.error || `Stripe could not be read (${res.status}).`)
    err.status = res.status
    throw err
  }
  return data
}
